import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import lodashPickBy from 'lodash/fp/pickBy';

import { USER_ROLE_ADMIN } from 'common/userRoles';

import { SCREEN_ID_PARTNER_SETTINGS_ADMIN_SETTINGS } from 'client/constants/screenIds';

import postWithState from 'client/actions/postWithState';
import {
  SR_API_ACTION_ADMIN_UPDATE_ITEM,
  SR_API_ACTION_BILLING_FEES_UPSERT,
} from 'api/actionTypes';

import { makeListingFullAddressString } from 'common/listingHelpers';

import withApiState from 'client/helpers/withApiState';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withLocationQuery from 'client/helpers/withLocationQuery';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles, { getUserLandingPage } from 'client/helpers/allowedRoles';
import branch from 'client/core/branch';

import PartnerActions from 'client/actions/Partner';

import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import { billingFeesFieldGroup } from 'server/models/BillingFees/fieldgroups';
import SrPartnerSettingsAdminForm from 'client/components/SrPartnerSettingsAdminForm/SrPartnerSettingsAdminForm';
import {
  actionDataTableLoadItems,
  actionDataTableLoadItemsIfNotFullyLoaded,
  actionInitializeDataTableState,
  dataTable,
} from 'client/helpers/dataTables';
import { DATA_TABLE_SOURCE_PARTNER_LISTINGS } from 'common/dataTableConstants';
import setNullEmptyValues from 'common/util/setNullEmptyValues';

function getListingDataTableSettings({ partner, match }) {
  const listingId = parseInt(match.params.listingId, 10);
  if (!partner) {
    return null;
  }
  const dataTableSettings = {
    itemsPropName: 'listing',
    dataSource: DATA_TABLE_SOURCE_PARTNER_LISTINGS,
    viewId: `by-listingId-${listingId}`,
    count: 1,
    injectSingleItem: true,
    filter: {
      'listings.id': parseInt(listingId, 10),
      'listings.partnerId': partner.id,
    },
    customParams: {
      partnerListings: {},
    },
  };
  return dataTableSettings;
}

@withTransitionHook
@allowedRoles({
  roles: [USER_ROLE_ADMIN],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_PARTNER_SETTINGS_ADMIN_SETTINGS)
@withScrollToTop
@branch(({ match }) => ({
  partner: ['partners', 'bySlug', match.params.partnerSlug],
  currentUser: ['currentUser'],
  accountManagersList: ['accountManagers', 'list'],
}))
@withApiState(({ match }) => ({
  adminUpdateBillingFeesApiState: {
    action: SR_API_ACTION_BILLING_FEES_UPSERT,
    queryId: `billingFees-${match.params.partnerSlug}-listing-${match.params.listingId}`,
  },
}))
@dataTable(getListingDataTableSettings)
@withLocationQuery
class PartnerEditListingAdminSettingsPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Admin Settings',
    section: 'Partner',
  });

  static fetchData(match, inject) {
    const partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);
    const promises = [];

    if (!partner) {
      promises.push(
        PartnerActions.loadPartnerBySlug(inject, {
          slug: match.params.partnerSlug,
        }),
      );
    }

    const listingDataTableSettings = getListingDataTableSettings({ partner, match });
    if (listingDataTableSettings) {
      actionInitializeDataTableState(inject, listingDataTableSettings);
      promises.push(actionDataTableLoadItemsIfNotFullyLoaded(inject, listingDataTableSettings));
    }

    return Promise.all(promises);
  }

  static propTypes = {
    partner: PropTypes.object,
    currentUser: PropTypes.object.isRequired,
    isTransitioning: PropTypes.bool.isRequired,
  };

  _handleBillingFeesUpdate = (values) => {
    const { partner, dispatch, match } = this.props;
    const filteredValues = setNullEmptyValues(
      lodashPickBy((_, key) => {
        return billingFeesFieldGroup.includes(key);
      }, values),
    );

    const listingId = parseInt(match.params.listingId, 10);

    dispatch(postWithState, {
      action: SR_API_ACTION_BILLING_FEES_UPSERT,
      queryId: `billingFees-${partner.slug}-listing-${listingId}`,
      payload: {
        listingId,
        billingFees: filteredValues,
      },
    });
  };

  _handleListingSettingUpdate = (values) => {
    const { dispatch, listing } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      queryId: `listing-${listing.id}`,
      payload: {
        table: 'listings',
        where: {
          id: listing.id,
        },
        updates: {
          highPriority: values.highPriority,
          partnerTaskCutLawnEnabled: values.partnerTaskCutLawnEnabled,
          partnerTaskCleanEnabled: values.partnerTaskCleanEnabled,
        },
      },
    });
  };

  _reloadPartnerListing = () => {
    const { partner, match, dispatch } = this.props;
    return Promise.all([
      dispatch(
        actionDataTableLoadItems,
        getListingDataTableSettings({
          partner,
          match,
        }),
      ),
    ]);
  };

  _onSubmit = (values) => {
    Promise.all([
      this._handleBillingFeesUpdate(values),
      this._handleListingSettingUpdate(values),
    ]).then(() => this._reloadPartnerListing());
  };

  render() {
    const {
      match,
      listing,
      partner,
      currentUser,
      isTransitioning,
      adminUpdateBillingFeesApiState,
    } = this.props;

    const listingId = parseInt(match.params.listingId, 10);

    if (isTransitioning && (!partner || !listing)) {
      return null;
    }

    if (!partner || !listing) {
      const userLandingPage = getUserLandingPage({
        userRoles: currentUser.roles,
      });
      return <Redirect to={userLandingPage} />;
    }

    return (
      <SrPartnerPageV2
        backUrl={`/partner/${match.params.partnerSlug}/listing/${listingId}`}
        navbarTitle={makeListingFullAddressString(listing)}
        pageHeader="Admin Settings"
      >
        <SrPartnerSettingsAdminForm
          partner={partner}
          listing={listing}
          billingFees={listing.billingFees || {}}
          formType="listing"
          onSubmit={this._onSubmit}
          inProgress={adminUpdateBillingFeesApiState.inProgress}
        />
      </SrPartnerPageV2>
    );
  }
}

PartnerEditListingAdminSettingsPage.displayName = 'PartnerEditListingAdminSettingsPage';

export default PartnerEditListingAdminSettingsPage;
